import { createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Hook that provides agents API methods with proper Clerk token handling.
 * Uses refs and callbacks to ensure stable function references across renders.
 */
export function useAgentsApi() {
  const { getToken } = useAuth();

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Create a stable token getter that always uses the latest getToken
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const agentsApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    return {
      getDashboard: () =>
        client.get<{
          agent: { id: string; name: string; territory: string };
          metrics: {
            total_affiliates: number;
            active_affiliates: number;
            pending_approvals: number;
            total_commission: number;
            pending_commission: number;
          };
        }>('/agents/dashboard'),

      getAffiliates: (params?: {
        status?: string;
        cursor?: string;
        limit?: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.set('status', params.status);
        if (params?.cursor) searchParams.set('cursor', params.cursor);
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        return client.get<{
          affiliates: Array<{
            id: string;
            name: string;
            address: string | null;
            country_code: string | null;
            subscription_status: 'pending' | 'active' | 'past_due' | 'cancelled';
            stripe_subscription_id: string | null;
            join_date: string;
            member_count: number;
            owner: { name: string; email: string };
          }>;
          pagination: { has_more: boolean; next_cursor: string | null };
          total_count: number;
          is_empty?: boolean;
          message?: string;
        }>(`/agents/affiliates?${searchParams}`);
      },

      getAffiliateDetail: (affiliateId: string) =>
        client.get<{
          affiliate: {
            id: string;
            name: string;
            address: string | null;
            country_code: string | null;
            subscription_status: string | null;
            has_stripe_subscription: boolean;
            subscription_tier: string | null;
            created_at: string;
            owner: { name: string; email: string; phone: string | null };
            member_count: number;
          };
        }>(`/agents/affiliates/${affiliateId}`),

      getCommissionSummary: () =>
        client.get<{
          months: Array<{
            period: string;
            total: number;
            equipment: number;
            affiliate_subs: number;
            individual_subs: number;
            entries: number;
          }>;
          grand_total: number;
        }>('/agents/commission/summary'),

      getCommissionLedger: (params?: {
        start_date?: string;
        end_date?: string;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        return client.get<{
          entries: Array<{
            id: string;
            type: string;
            description: string;
            amount_cents: number;
            period_month: string;
            created_at: string;
          }>;
          totals: { total: number; by_type: Record<string, number> };
        }>(`/agents/commission?${searchParams}`);
      },

      getProfile: () =>
        client.get<{
          agent: {
            id: string;
            name: string;
            territory: string;
            shopify_affiliate_code: string;
            commission_rate_subs: number;
            commission_rate_equipment: number;
          };
        }>('/agents/profile'),
    };
  }, [stableGetToken]);

  return agentsApi;
}
